import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { recordImageLoadDiagnostic } from "../shared/image-load-diagnostics";

/** 画像 DOM の読み込みを監視する。保存済み URL・共有状態・編集履歴には手を加えない。 */
export function useImageLoadState(
  imageRef: RefObject<HTMLImageElement | null>,
  src: string,
  isPending: boolean
): { readonly retry: () => void; readonly status: ImageLoadStatus } {
  const [loadState, setLoadState] = useState<{
    readonly src: string;
    readonly status: ImageLoadStatus;
  }>();
  const retryRef = useRef<(() => void) | undefined>(undefined);
  const retry = useCallback(() => retryRef.current?.(), []);

  useEffect(() => {
    const image = imageRef.current;
    if (!image) return undefined;
    const startedAt = performance.now();
    // data: は他参加者に配信するアップロード待ちの LQIP。取得し直しても本体にはならない。
    if (isPending) {
      // 取得の停滞とアップロード確定待ちを区別する。再送・状態変更はせず一度だけ記録する。
      const pendingTimer = setTimeout(() => {
        recordImageLoadDiagnostic("pending-timeout", image, 0, performance.now() - startedAt);
      }, 30_000);
      return () => clearTimeout(pendingTimer);
    }

    // StrictMode の再購読では、再試行待ちで src を外した DOM が残る場合がある。
    // cleanup では ref が一時的に null になり得るため、次の購読側で現在の src を戻す。
    // unmount や別 src への切替時に、古い取得を再開しないようにする。
    if (!image.hasAttribute("src")) image.setAttribute("src", src);

    // 先に待機へ移り、その直後に DOM の完了を確認する。A → B → A の切替でも、以前の
    // A の成功状態を新しい取得へ流用しない（購読前に完了済みなら下の確認で回収する）。
    setLoadState({ src, status: "loading" });

    let disposed = false;
    let settled = false;
    let retries = 0;
    let experiencedFailure = false;
    let recoveryRecorded = false;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const clearTimers = (): void => {
      clearTimeout(timeout);
      clearTimeout(retryTimer);
      timeout = undefined;
      retryTimer = undefined;
    };
    const isCurrent = (): boolean => !disposed && image.getAttribute("src") === src;
    const loaded = (): void => {
      if (!isCurrent()) return;
      // complete は取得失敗時にも true になるため、実画像の寸法も併せて確認する。
      if (!image.complete || image.naturalWidth === 0) return;
      if (experiencedFailure && !recoveryRecorded) {
        recordImageLoadDiagnostic("recovered", image, retries, performance.now() - startedAt);
        recoveryRecorded = true;
      }
      settled = true;
      clearTimers();
      setLoadState({ src, status: "loaded" });
    };
    const startReload = (delay: number): void => {
      clearTimers();
      // DOM を差し替えると useObjectDrag が握る要素・イベント購読が切れるため同じ img を使う。
      // 一度 src を外し、別タスクで同じ URL を設定して停滞中の取得もやり直す。
      // 署名付き URL を壊さないようキャッシュ回避のクエリは付けない。
      image.removeAttribute("src");
      retryTimer = setTimeout(() => {
        retryTimer = undefined;
        // src 変更後の React commit と effect の cleanup の隙間も旧 URL で上書きしない。
        if (disposed || image.hasAttribute("src")) return;
        image.setAttribute("src", src);
        timeout = setTimeout(checkCompletion, IMAGE_LOAD_TIMEOUT_MS);
      }, delay);
    };
    const failed = (reason: "error" | "timeout"): void => {
      if (!isCurrent() || settled) return;
      experiencedFailure = true;
      recordImageLoadDiagnostic(reason, image, retries, performance.now() - startedAt);
      clearTimers();
      if (retries < IMAGE_AUTO_RETRY_LIMIT) {
        const delay = 1_000 * 2 ** retries + Math.random() * 250;
        retries += 1;
        startReload(delay);
      } else {
        settled = true;
        setLoadState({ src, status: "errored" });
      }
    };
    const checkCompletion = (): void => {
      if (image.complete && image.naturalWidth > 0) loaded();
      else failed("timeout");
    };
    const onError = (): void => failed("error");
    image.addEventListener("load", loaded);
    image.addEventListener("error", onError);
    retryRef.current = () => {
      if (disposed) return;
      recordImageLoadDiagnostic("manual-retry", image, retries, performance.now() - startedAt);
      retries = 0;
      recoveryRecorded = false;
      settled = false;
      setLoadState({ src, status: "loading" });
      startReload(0);
    };

    // キャッシュ済み画像では購読前に load が終わり得る。後追いの false リセットではなく、
    // DOM の完了状態を取り込む。未完了の取得には期限を設け、ぼかしを無期限に残さない。
    if (image.complete && image.naturalWidth > 0) loaded();
    else if (image.complete) failed("error");
    else timeout = setTimeout(checkCompletion, IMAGE_LOAD_TIMEOUT_MS);

    return () => {
      disposed = true;
      clearTimers();
      retryRef.current = undefined;
      image.removeEventListener("load", loaded);
      image.removeEventListener("error", onError);
    };
  }, [imageRef, isPending, src]);

  return { retry, status: loadState?.src === src ? loadState.status : "loading" };
}

//------------------------------------------------------------------------------
// Helpers
//------------------------------------------------------------------------------

const IMAGE_AUTO_RETRY_LIMIT = 2;
const IMAGE_LOAD_TIMEOUT_MS = 15_000;
type ImageLoadStatus = "loading" | "loaded" | "errored";
